import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { useEffect, useRef } from "react";
import type { ApAgentRun, ApDocumentType } from "@/accounts-payable/types";
import { quickbooksConnectionKey } from "@/business/quickbooks-query";
import { useApi } from "@/core/api";
import { documentKey } from "./document-key";
import {
  AP_DOCUMENT_ENDPOINTS,
  documentAgentRunsKey,
  invoiceQuickbooksBillKey,
} from "./keys";
import { AP_STALE_TIME } from "./polling";

type RunsRefetchInterval =
  | number
  | false
  | ((query: { state: { data?: ApAgentRun[] } }) => number | false);

export const documentAgentRunsQueryOptions = (
  api: AxiosInstance,
  businessId: string | null | undefined,
  documentType: ApDocumentType,
  documentId: string | null | undefined,
) =>
  queryOptions({
    queryKey: documentAgentRunsKey(businessId, documentId),
    queryFn: () =>
      api
        .get<ApAgentRun[]>(`/businesses/${businessId}/ap/agent_runs`, {
          params: {
            [AP_DOCUMENT_ENDPOINTS[documentType].runsParam]: documentId,
          },
        })
        .then((response) => response.data),
    enabled: !!businessId && !!documentId,
    staleTime: AP_STALE_TIME,
  });

export const invoiceAgentRunsQueryOptions = (
  api: AxiosInstance,
  businessId: string | null | undefined,
  invoiceId: string | null | undefined,
) => documentAgentRunsQueryOptions(api, businessId, "invoice", invoiceId);

export const agentRunsReviewSignature = (
  runs: ApAgentRun[] | undefined,
): string | null =>
  runs
    ? runs
        .map((run) =>
          [
            run.id,
            run.status,
            ...run.actions.map(
              (action) =>
                `${action.id}:${action.status}:${action.rollback_status ?? ""}:${action.waiting_reason ?? ""}`,
            ),
          ].join("|"),
        )
        .join(";")
    : null;

export const useGetDocumentAgentRuns = (
  businessId: string | null | undefined,
  documentType: ApDocumentType,
  documentId: string | null | undefined,
  refetchInterval?: RunsRefetchInterval,
) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const query = useQuery({
    ...documentAgentRunsQueryOptions(api, businessId, documentType, documentId),
    refetchInterval,
  });

  const signature = agentRunsReviewSignature(query.data);
  const hasWaitingBill = !!query.data?.some((run) =>
    run.actions.some((action) => action.waiting_reason),
  );
  const seenSignature = useRef<string | null>(null);
  useEffect(() => {
    if (signature === null) return;
    const previous = seenSignature.current;
    seenSignature.current = signature;
    if (previous === null || previous === signature) return;

    void queryClient.invalidateQueries({
      queryKey: documentKey(documentType, businessId, documentId),
      exact: true,
    });
    if (documentType === "invoice")
      void queryClient.invalidateQueries({
        queryKey: invoiceQuickbooksBillKey(businessId, documentId),
      });
    if (hasWaitingBill)
      void queryClient.invalidateQueries({
        queryKey: quickbooksConnectionKey(businessId),
      });
  }, [
    signature,
    hasWaitingBill,
    queryClient,
    documentType,
    businessId,
    documentId,
  ]);

  return query;
};

export const useGetInvoiceAgentRuns = (
  businessId?: string | null,
  invoiceId?: string | null,
  refetchInterval?: RunsRefetchInterval,
) => useGetDocumentAgentRuns(businessId, "invoice", invoiceId, refetchInterval);
